import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Multi-select chip picker.
 * - `options`: [{id, title}, ...]
 * - `value`: array of selected ids
 * - `onChange(newIds)`: callback with new array of ids
 * - `placeholder`: input placeholder when empty
 *
 * Single-select variant: pass `single` and value/onChange will deal with one id (or null).
 */
const MultiPicker = ({
  options = [],
  value,
  onChange,
  placeholder = "Type to search…",
  single = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedIds = useMemo(() => {
    if (single) return value ? [value] : [];
    return Array.isArray(value) ? value : [];
  }, [value, single]);

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedIds.includes(o.id)),
    [options, selectedIds]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => !selectedIds.includes(o.id))
      .filter((o) => !q || (o.title || "").toLowerCase().includes(q))
      .slice(0, 50);
  }, [options, selectedIds, query]);

  const add = (option) => {
    if (single) {
      onChange(option.id);
    } else {
      onChange([...selectedIds, option.id]);
    }
    setQuery("");
    if (single) setOpen(false);
  };

  const remove = (id) => {
    if (single) {
      onChange(null);
    } else {
      onChange(selectedIds.filter((x) => x !== id));
    }
  };

  return (
    <div className="ws-picker-wrapper" ref={wrapperRef}>
      <div
        className="ws-picker"
        onClick={() => !disabled && setOpen(true)}
        style={disabled ? { opacity: 0.6, pointerEvents: "none" } : {}}
      >
        {selectedOptions.map((o) => (
          <span className="ws-chip" key={o.id}>
            {o.title}
            <button
              type="button"
              className="ws-chip-x"
              onClick={(e) => {
                e.stopPropagation();
                remove(o.id);
              }}
            >
              ×
            </button>
          </span>
        ))}
        {(!single || selectedOptions.length === 0) && (
          <input
            className="ws-picker-input"
            placeholder={selectedOptions.length ? "" : placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />
        )}
      </div>
      {open && (
        <div className="ws-picker-suggestions">
          {filtered.length === 0 ? (
            <div className="ws-picker-suggestion empty">No matches</div>
          ) : (
            filtered.map((o) => (
              <div
                key={o.id}
                className="ws-picker-suggestion"
                onClick={() => add(o)}
              >
                {o.title}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MultiPicker;
